import { normalize } from '@jianghu/shared';
import * as THREE from 'three';
import { glowTexture, taijiTexture } from '../world/textures';
import { ParticleSystem } from './particles';

interface Effect {
  update(dt: number): boolean;
  dispose(): void;
}

const additive = (color: THREE.ColorRepresentation, opacity = 1): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

/** Arc ribbon shader: a bright band sweeps along the arc and fades, brightest at the blade tip. */
function arcMaterial(
  color: THREE.Color,
  inner: number,
  outer: number,
  thetaStart: number,
  thetaLength: number,
  reverse: boolean,
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: {
      uColor: { value: color },
      uHead: { value: 0 },
      uFade: { value: 1 },
      uInner: { value: inner },
      uOuter: { value: outer },
      uStart: { value: thetaStart },
      uLen: { value: thetaLength },
      uReverse: { value: reverse ? 1 : 0 },
    },
    vertexShader: `varying vec2 vP; void main(){ vP = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: `
      uniform vec3 uColor; uniform float uHead; uniform float uFade; uniform float uInner; uniform float uOuter;
      uniform float uStart; uniform float uLen; uniform float uReverse; varying vec2 vP;
      void main(){
        float a = atan(vP.y, vP.x);
        float t = (a - uStart) / uLen;
        t = fract(t + 1.0);
        if (uReverse > 0.5) t = 1.0 - t;
        float r = (length(vP) - uInner) / (uOuter - uInner);
        float band = smoothstep(uHead - 0.45, uHead, t) * (1.0 - smoothstep(uHead, uHead + 0.04, t));
        float edge = pow(clamp(r, 0.0, 1.0), 1.6) * (1.0 - smoothstep(0.92, 1.0, r));
        float alpha = band * edge * uFade;
        vec3 col = mix(uColor, vec3(1.0), pow(r, 5.0) * 0.85 + band * 0.15);
        gl_FragColor = vec4(col * 1.8, alpha);
      }`,
  });
}

export interface ArcOptions {
  color: THREE.ColorRepresentation;
  radius: number;
  width?: number;
  arcDeg?: number;
  /** Seconds for the sweep. */
  sweep?: number;
  tilt?: number;
  reverse?: boolean;
  height?: number;
}

/**
 * Visual effects manager: reusable effect primitives (arcs, streaks, projectiles, ground
 * formations, shockwaves, telegraphs, pillars) plus a shared particle system.
 */
export class Vfx {
  readonly group = new THREE.Group();
  readonly particles = new ParticleSystem(3000);
  private effects: Effect[] = [];
  private readonly glow = glowTexture();
  private taijiTex: THREE.Texture | null = null;
  /** Optional collision test so projectile visuals stop where the server's do. */
  blocked: ((x: number, z: number) => boolean) | null = null;
  maxEffects = 80;

  constructor() {
    this.group.name = 'vfx';
    this.group.add(this.particles.points);
  }

  private add(e: Effect): void {
    if (this.effects.length >= this.maxEffects) {
      const old = this.effects.shift();
      old?.dispose();
    }
    this.effects.push(e);
  }

  update(dt: number): void {
    const keep: Effect[] = [];
    for (const e of this.effects) {
      if (e.update(dt)) keep.push(e);
      else e.dispose();
    }
    this.effects = keep;
    this.particles.update(dt);
  }

  get activeCount(): number {
    return this.effects.length;
  }

  // ---------------------------------------------------------------- primitives

  /** Sword slash arc in front of a character. */
  swordArc(pos: THREE.Vector3, yaw: number, o: ArcOptions): void {
    const width = o.width ?? 0.9;
    const arc = ((o.arcDeg ?? 150) * Math.PI) / 180;
    const start = -Math.PI / 2 - arc / 2;
    const geo = new THREE.RingGeometry(Math.max(0.05, o.radius - width), o.radius, 40, 1, start, arc);
    const mat = arcMaterial(
      new THREE.Color(o.color),
      Math.max(0.05, o.radius - width),
      o.radius,
      start,
      arc,
      o.reverse ?? false,
    );
    const mesh = new THREE.Mesh(geo, mat);
    const holder = new THREE.Group();
    holder.position.copy(pos).setY(pos.y + (o.height ?? 1.05));
    holder.rotation.set(0, yaw, 0, 'YXZ');
    const tiltG = new THREE.Group();
    tiltG.rotation.z = o.tilt ?? 0;
    mesh.rotation.x = -Math.PI / 2;
    tiltG.add(mesh);
    holder.add(tiltG);
    this.group.add(holder);
    const sweep = o.sweep ?? 0.16;
    let t = 0;
    this.add({
      update: (dt) => {
        t += dt;
        mat.uniforms.uHead!.value = Math.min(1.35, (t / sweep) * 1.35);
        mat.uniforms.uFade!.value = t < sweep ? 1 : Math.max(0, 1 - (t - sweep) / 0.22);
        return t < sweep + 0.22;
      },
      dispose: () => {
        this.group.remove(holder);
        geo.dispose();
        mat.dispose();
      },
    });
  }

  /** Straight energy streak (thrusts). */
  streak(
    from: THREE.Vector3,
    dir: THREE.Vector3,
    length: number,
    color: THREE.ColorRepresentation,
    width = 0.35,
  ): void {
    const geo = new THREE.ConeGeometry(width, length, 10, 1, true);
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, 0, length / 2);
    const mat = additive(color, 0.9);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(from);
    mesh.lookAt(from.clone().add(dir));
    const core = new THREE.Mesh(geo, additive('#ffffff', 0.8));
    core.scale.set(0.35, 0.35, 1);
    mesh.add(core);
    this.group.add(mesh);
    let t = 0;
    this.add({
      update: (dt) => {
        t += dt;
        const u = t / 0.28;
        mesh.scale.set(1 - u * 0.6, 1 - u * 0.6, 0.6 + Math.min(1, u * 4) * 0.5);
        mat.opacity = Math.max(0, 0.95 * (1 - u));
        (core.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.8 * (1 - u));
        return u < 1;
      },
      dispose: () => {
        this.group.remove(mesh);
        geo.dispose();
        mat.dispose();
        (core.material as THREE.Material).dispose();
      },
    });
  }

  /** Expanding ground shockwave ring. */
  shockwave(pos: THREE.Vector3, radius: number, color: THREE.ColorRepresentation, duration = 0.45): void {
    const geo = new THREE.RingGeometry(0.85, 1, 64);
    const mat = additive(color, 0.9);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.copy(pos).setY(pos.y + 0.06);
    this.group.add(mesh);
    let t = 0;
    this.add({
      update: (dt) => {
        t += dt;
        const u = t / duration;
        const s = 0.3 + (radius - 0.3) * (1 - Math.pow(1 - Math.min(1, u), 3));
        mesh.scale.set(s, s, s);
        mat.opacity = Math.max(0, 0.9 * (1 - u));
        return u < 1;
      },
      dispose: () => {
        this.group.remove(mesh);
        geo.dispose();
        mat.dispose();
      },
    });
  }

  /** Bright flash sprite. */
  flash(pos: THREE.Vector3, color: THREE.ColorRepresentation, size = 1.6, duration = 0.18): void {
    const mat = new THREE.SpriteMaterial({
      map: this.glow,
      color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    });
    const s = new THREE.Sprite(mat);
    s.position.copy(pos);
    s.scale.setScalar(size);
    this.group.add(s);
    let t = 0;
    this.add({
      update: (dt) => {
        t += dt;
        const u = t / duration;
        s.scale.setScalar(size * (0.7 + u * 0.6));
        mat.opacity = Math.max(0, 1 - u);
        return u < 1;
      },
      dispose: () => {
        this.group.remove(s);
        mat.dispose();
      },
    });
  }

  impact(pos: THREE.Vector3, color: THREE.ColorRepresentation, crit: boolean): void {
    this.flash(pos, color, crit ? 2.6 : 1.5, crit ? 0.25 : 0.16);
    this.particles.emit({
      count: crit ? 26 : 14,
      origin: pos,
      spread: 0.15,
      velocityRandom: crit ? 9 : 6,
      rise: 2,
      colors: [color, '#ffffff', '#ffe6a8'],
      size: [0.08, crit ? 0.22 : 0.15],
      life: [0.25, 0.5],
      gravity: 6,
      drag: 3,
    });
  }

  /** Flying crescent of sword qi. Travels until `distance` or an obstacle. */
  crescent(
    origin: THREE.Vector3,
    dx: number,
    dz: number,
    speed: number,
    distance: number,
    width: number,
    color: THREE.ColorRepresentation,
  ): void {
    const d = normalize(dx, dz);
    const shape = new THREE.Shape();
    shape.absarc(0, 0, width / 2, Math.PI * 0.08, Math.PI * 0.92, false);
    shape.absarc(0, -width * 0.16, (width / 2) * 0.9, Math.PI * 0.9, Math.PI * 0.1, true);
    const geo = new THREE.ShapeGeometry(shape, 24);
    geo.translate(0, -width * 0.15, 0);
    const mat = additive(color, 0.95);
    const outer = new THREE.Mesh(geo, mat);
    const coreMat = additive('#fffbe8', 0.9);
    const core = new THREE.Mesh(geo, coreMat);
    core.scale.set(0.82, 0.55, 1);
    core.position.z = 0.02;
    const holder = new THREE.Group();
    holder.add(outer, core);
    // Crescent stands upright, perpendicular to travel, bowed forward.
    outer.rotation.set(-Math.PI / 2 + 0.35, 0, 0);
    core.rotation.copy(outer.rotation);
    holder.position.set(origin.x, origin.y + 1.0, origin.z);
    holder.rotation.y = Math.atan2(d.x, d.z);
    this.group.add(holder);
    let travelled = 0;
    let t = 0;
    let dying = -1;
    this.add({
      update: (dt) => {
        t += dt;
        if (dying < 0) {
          const step = speed * dt;
          holder.position.x += d.x * step;
          holder.position.z += d.z * step;
          travelled += step;
          if (travelled >= distance || this.blocked?.(holder.position.x, holder.position.z)) dying = 0;
          this.particles.emit({
            count: 4,
            origin: holder.position,
            spread: width * 0.4,
            velocity: new THREE.Vector3(-d.x * 2, 0.4, -d.z * 2),
            velocityRandom: 1.2,
            colors: [color, '#fff3c2'],
            size: [0.1, 0.24],
            life: [0.25, 0.5],
            drag: 2,
          });
        } else dying += dt;
        const pulse = 1 + Math.sin(t * 30) * 0.06;
        holder.scale.set(pulse, pulse, pulse);
        const fade = dying < 0 ? Math.min(1, t * 10) : Math.max(0, 1 - dying / 0.15);
        mat.opacity = 0.95 * fade;
        coreMat.opacity = 0.9 * fade;
        return dying < 0.15;
      },
      dispose: () => {
        this.group.remove(holder);
        geo.dispose();
        mat.dispose();
        coreMat.dispose();
      },
    });
  }

  /**
   * Taiji formation: a turning yin-yang/trigram diagram grows under the caster during the wind-up,
   * erupts at `releaseAt` (shockwave, light pillar, sparks), then fades.
   */
  taiji(pos: THREE.Vector3, radius: number, releaseAt: number, color: THREE.ColorRepresentation): void {
    this.taijiTex ??= taijiTexture('#ffffff');
    const mat = new THREE.MeshBasicMaterial({
      map: this.taijiTex,
      color,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0,
    });
    const geo = new THREE.PlaneGeometry(2, 2);
    const disk = new THREE.Mesh(geo, mat);
    disk.rotation.x = -Math.PI / 2;
    disk.position.copy(pos).setY(pos.y + 0.05);
    this.group.add(disk);
    const pillarGeo = new THREE.CylinderGeometry(radius * 0.25, radius * 0.45, 7, 24, 1, true);
    pillarGeo.translate(0, 3.5, 0);
    const pillarMat = additive(color, 0);
    const pillar = new THREE.Mesh(pillarGeo, pillarMat);
    pillar.position.copy(pos);
    this.group.add(pillar);
    let t = 0;
    let released = false;
    this.add({
      update: (dt) => {
        t += dt;
        const grow = Math.min(1, t / releaseAt);
        const s = radius * (0.25 + 0.75 * (1 - Math.pow(1 - grow, 2)));
        disk.scale.set(s, s, s);
        disk.rotation.z += dt * (released ? 1.2 : 2.6);
        if (!released) {
          mat.opacity = 0.35 + grow * 0.5;
          if (Math.random() < 0.6) {
            const a = Math.random() * Math.PI * 2;
            this.particles.emit({
              count: 2,
              origin: new THREE.Vector3(pos.x + Math.sin(a) * s, pos.y + 0.1, pos.z + Math.cos(a) * s),
              velocity: new THREE.Vector3(0, 2.5, 0),
              velocityRandom: 0.6,
              colors: [color, '#fff6d8'],
              size: [0.1, 0.2],
              life: [0.4, 0.8],
            });
          }
          if (t >= releaseAt) {
            released = true;
            this.shockwave(pos, radius * 1.15, color, 0.42);
            this.shockwave(pos, radius * 0.8, '#ffffff', 0.32);
            this.flash(pos.clone().setY(pos.y + 1), color, 4, 0.3);
            this.particles.emit({
              count: 60,
              origin: pos.clone().setY(pos.y + 0.3),
              spread: radius * 0.5,
              radial: 8,
              rise: 4,
              velocityRandom: 2,
              colors: [color, '#ffffff', '#ffe6a8'],
              size: [0.12, 0.3],
              life: [0.4, 0.9],
              gravity: 3,
              drag: 2.5,
            });
          }
        } else {
          const u = (t - releaseAt) / 0.9;
          mat.opacity = Math.max(0, 0.95 * (1 - u));
          pillarMat.opacity = Math.max(0, 0.55 * (1 - u * 2.2));
          pillar.scale.set(1 + u * 0.4, 1 + u * 0.3, 1 + u * 0.4);
          if (u >= 1) return false;
        }
        return true;
      },
      dispose: () => {
        this.group.remove(disk, pillar);
        geo.dispose();
        mat.dispose();
        pillarGeo.dispose();
        pillarMat.dispose();
      },
    });
  }

  /** Qi gathering around a point (wind-up glow) for `duration` seconds. */
  charge(follow: () => THREE.Vector3 | null, color: THREE.ColorRepresentation, duration: number): void {
    let t = 0;
    const mat = new THREE.SpriteMaterial({
      map: this.glow,
      color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0,
    });
    const s = new THREE.Sprite(mat);
    this.group.add(s);
    this.add({
      update: (dt) => {
        t += dt;
        const p = follow();
        if (!p) return false;
        s.position.copy(p);
        const u = t / duration;
        s.scale.setScalar(0.4 + u * 0.9);
        mat.opacity = Math.min(1, u * 1.5) * (u > 0.9 ? (1 - u) * 10 : 1);
        if (Math.random() < 0.5) {
          const a = Math.random() * Math.PI * 2;
          const off = new THREE.Vector3(Math.cos(a) * 0.7, (Math.random() - 0.5) * 0.6, Math.sin(a) * 0.7);
          this.particles.emit({
            count: 1,
            origin: p.clone().add(off),
            velocity: off.multiplyScalar(-3),
            velocityRandom: 0.2,
            colors: [color, '#ffffff'],
            size: [0.06, 0.12],
            life: [0.2, 0.3],
            drag: 0,
          });
        }
        return u < 1;
      },
      dispose: () => {
        this.group.remove(s);
        mat.dispose();
      },
    });
  }

  /** Red ground warning for telegraphed enemy attacks; fills up until the hit lands. */
  telegraph(
    pos: THREE.Vector3,
    dx: number,
    dz: number,
    shape: 'arc' | 'circle',
    radius: number,
    halfAngleDeg: number,
    ms: number,
  ): void {
    const half = shape === 'circle' ? Math.PI : (halfAngleDeg * Math.PI) / 180;
    const start = -Math.PI / 2 - half;
    const geo = new THREE.CircleGeometry(radius, 48, start, half * 2);
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { uFill: { value: 0 }, uR: { value: radius }, uAlpha: { value: 1 } },
      vertexShader: `varying vec2 vP; void main(){ vP = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
      fragmentShader: `
        uniform float uFill; uniform float uR; uniform float uAlpha; varying vec2 vP;
        void main(){
          float r = length(vP) / uR;
          float edge = smoothstep(0.9, 1.0, r);
          float fill = step(r, uFill);
          float a = (0.18 + edge * 0.55 + fill * 0.35) * uAlpha;
          gl_FragColor = vec4(mix(vec3(0.9, 0.15, 0.1), vec3(1.0, 0.45, 0.2), fill), a);
        }`,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.set(-Math.PI / 2, 0, 0);
    const holder = new THREE.Group();
    holder.position.copy(pos).setY(pos.y + 0.04);
    holder.rotation.y = Math.atan2(dx, dz);
    holder.add(mesh);
    this.group.add(holder);
    const dur = ms / 1000;
    let t = 0;
    this.add({
      update: (dt) => {
        t += dt;
        mat.uniforms.uFill!.value = Math.min(1, t / dur);
        mat.uniforms.uAlpha!.value = t < dur ? 1 : Math.max(0, 1 - (t - dur) / 0.2);
        return t < dur + 0.2;
      },
      dispose: () => {
        this.group.remove(holder);
        geo.dispose();
        mat.dispose();
      },
    });
  }

  /** Golden rings and rising sparks on level-up. */
  levelUp(follow: () => THREE.Vector3 | null): void {
    let t = 0;
    const rings: THREE.Mesh[] = [];
    const geo = new THREE.TorusGeometry(0.9, 0.04, 6, 48);
    const mats: THREE.MeshBasicMaterial[] = [];
    for (let i = 0; i < 3; i++) {
      const m = additive('#ffd77a', 0.9);
      mats.push(m);
      const r = new THREE.Mesh(geo, m);
      r.rotation.x = Math.PI / 2;
      rings.push(r);
      this.group.add(r);
    }
    this.add({
      update: (dt) => {
        t += dt;
        const p = follow();
        if (!p) return false;
        rings.forEach((r, i) => {
          const u = Math.max(0, Math.min(1, (t - i * 0.18) / 1.2));
          r.position.set(p.x, p.y + u * 2.4, p.z);
          r.scale.setScalar(1 - u * 0.4);
          (mats[i] as THREE.MeshBasicMaterial).opacity = (1 - u) * 0.9;
        });
        if (t < 1 && Math.random() < 0.8) {
          this.particles.emit({
            count: 3,
            origin: p.clone().setY(p.y + 0.2),
            spread: 0.8,
            velocity: new THREE.Vector3(0, 3.5, 0),
            velocityRandom: 0.8,
            colors: ['#ffd77a', '#ffffff'],
            size: [0.1, 0.2],
            life: [0.6, 1.1],
            drag: 0.5,
          });
        }
        return t < 1.6;
      },
      dispose: () => {
        rings.forEach((r) => this.group.remove(r));
        geo.dispose();
        mats.forEach((m) => m.dispose());
      },
    });
  }

  teleport(pos: THREE.Vector3): void {
    this.flash(pos.clone().setY(pos.y + 1.2), '#7fffd0', 4.5, 0.5);
    this.particles.emit({
      count: 50,
      origin: pos.clone().setY(pos.y + 0.2),
      spread: 0.8,
      velocity: new THREE.Vector3(0, 5, 0),
      velocityRandom: 1.2,
      colors: ['#7fffd0', '#ffffff', '#3fe0a8'],
      size: [0.1, 0.25],
      life: [0.6, 1.2],
      drag: 0.8,
    });
    this.shockwave(pos, 2.5, '#7fffd0', 0.5);
  }

  heal(pos: THREE.Vector3, color: THREE.ColorRepresentation = '#7fff9a'): void {
    this.particles.emit({
      count: 22,
      origin: pos.clone().setY(pos.y + 0.4),
      spread: 0.6,
      velocity: new THREE.Vector3(0, 2.2, 0),
      velocityRandom: 0.6,
      colors: [color, '#ffffff'],
      size: [0.1, 0.2],
      life: [0.6, 1.0],
      drag: 0.6,
    });
  }

  dust(pos: THREE.Vector3, amount = 10): void {
    this.particles.emit({
      count: amount,
      origin: pos.clone().setY(pos.y + 0.1),
      spread: 0.4,
      radial: 2.5,
      velocityRandom: 0.8,
      rise: 0.8,
      colors: ['#bba98a', '#8f8068'],
      size: [0.25, 0.5],
      life: [0.4, 0.8],
      drag: 3,
    });
  }

  dispose(): void {
    for (const e of this.effects) e.dispose();
    this.effects = [];
    this.particles.dispose();
    this.glow.dispose();
    this.taijiTex?.dispose();
  }
}
